import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { T, TF } from "react-translator-component";
import {
  Box,
  Checkbox,
  FormControlLabel,
  SelectChangeEvent,
  Typography,
  CircularProgress,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EventApplicationDetails from "../../components/Phase2/EventApplicationDetails";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import SubmitButton from "../../components/SubmitButton";
import Separator from "../../components/Separator";
import MessageModal from "../../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import { colors } from "../../common/colors";
import appServices from "../../services/app-services/appServices";

const EventApplicationPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.userAuth);
  const { item, selectedValues } = location.state || {};
  const [checked, setChecked] = useState<boolean>(false);
  const detail = item?.post_events[0];
  const [isPaymentConfirmationModalOpen, setIsPaymentConfirmationModalOpen] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [finalValues, setFinalValues] = useState<any>(
    selectedValues?.map((value: any) => value)
  ); // Initialize with default values

  // Handle change for dropdowns
  const handleChange = (index: number) => (event: SelectChangeEvent) => {
    const newValues = [...selectedValues];
    newValues[index] = parseInt(event.target.value, 10);
    setFinalValues(newValues);
  };

  // Calculate the sum of all selected values
  const totalSum = finalValues.reduce((acc: any, value: any) => acc + value, 0);

  const createPayload = () => {
    const eventDetail = detail.participant_settings.map(
      (item: any, index: any) => {
        return {
          id: item.id,
          name: item.name,
          free: item.free,
          fee_amount: item.free ? null : item.fee_amount,
          number_of_participants: finalValues[index],
          total_amount: item.free ? 0 : item.fee_amount * finalValues[index],
          user_id: user?.id,
        };
      }
    );
    return eventDetail;
  };

  const AddEventActivity = async () => {
    try {
      setIsLoading(true);
      const eventPayload = createPayload();

      const body = {
        post_event_id: detail?.id,
        user_id: user?.id,
        status: "applied",
        application_details: eventPayload,
      };

      const res = await appServices.addEventActivity(body);
      setIsPaymentConfirmationModalOpen(true);
      setIsLoading(false);
      setTimeout(() => {
        navigate("/myfarm");
      }, 3000);
      return true
    } catch (error: any) {
      const responseError = error?.response?.data?.message
      setIsLoading(false);
      setIsError(true);
      setErrorMessage(
        typeof responseError === "object" ?
          error?.response?.data?.message.join("\n") :
          error?.response?.data?.message ?? `${T("api_unknown_error")}`
      );
      return false
    }
  };

  const calculateTotalAmount = () => {
    let Amount: number = 0;
    detail.participant_settings.map((item: any, index: any) => {
      if (item.free) return 0

      return (Amount += item.fee_amount * finalValues[index]);
    });

    return Amount;
  };

  return (
    <Box>
      <MessageModal
        open={isPaymentConfirmationModalOpen}
        message={T("event_application_payment_applied")}
        onClose={() => setIsPaymentConfirmationModalOpen(false)}
      />
      <ErrorMessageModal
        open={isError}
        onClose={() => setIsError(false)}
        messages={[errorMessage]}
        showButton
        alignLeft={errorMessage.indexOf("\n") !== -1}
      />
      <Box
        style={{
          paddingTop: 100,
          paddingLeft: 16,
          paddingBottom: "12px",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
        }}
      >
        <GCBackButton onClick={() => navigate(-1)} />
      </Box>
      <Box sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <EventApplicationDetails
          item={item}
          selectedValues={finalValues}
          handleChange={handleChange}
          dropdownDisabled={true}
        />
      </Box>
      <Box sx={{ padding: "20px 20px" }}>
        <Box sx={style.finalAmountBox}>
          <Box sx={{ display: "flex" }}>
            <Typography>{T("event_application_payment_persons_sum")}</Typography>
            <Typography sx={{ fontWeight: 600, paddingLeft: "5px" }}>
              {TF("{0}{1}", totalSum, T("unit_person"))}
            </Typography>
          </Box>
          <Box sx={{ display: "flex" }}>
            <Typography>{T("event_application_payment_fee_sum")}</Typography>
            <Typography
              sx={{ fontWeight: 600, color: "red", paddingLeft: "5px" }}
            >
              {TF("{0}{1}", calculateTotalAmount(), T("unit_yen"))}
            </Typography>
          </Box>
        </Box>

        <Separator sx={{ marginTop: "10px" }} color={colors.farm_gray100} />

        <Typography sx={{ color: colors.farm_gray200, paddingTop: "10px" }}>
          {T("event_application_payment_payment_method_is_mebuku_pay")}
          <br/>
          {T("event_application_payment_check_balance")}
        </Typography>
        <Separator sx={{ marginTop: "10px" }} color={colors.farm_gray100} />

        <FormControlLabel
          style={{ paddingTop: "30px" }}
          control={
            <Checkbox
              checked={checked}
              onChange={(event) => setChecked(event.target.checked)}
              name="singleCheckbox"
            />
          }
          label={T("event_application_payment_confirm")}
        />
        <SubmitButton
          disabled={!checked || isLoading}
          color="secondary"
          sx={style.submitButton}
          size={"large"}
          variant="outlined"
          validate={async () => (checked && !isLoading)}
          onSubmit={AddEventActivity}
          endIcon={
            <ArrowForwardIosIcon
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                margin: "10px",
              }}
            />
          }
        >
          {isLoading ? <CircularProgress size={20} /> : T("apply")}
        </SubmitButton>
      </Box>
    </Box>
  );
};

const style = {
  submitButton: {
    width: "80%",
    alignSelf: "center",
    margin: "0 auto",
    marginTop: "20px",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
  },
  finalAmountBox: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    paddingTop: "5px",
    paddingBottom: "10px",
    paddingLeft: "10px",
  },
};

export default EventApplicationPayment;
